import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import CybsIcon from '@cybs-ui/components/lib/icon';

const CSS_PREFIX = 'vds-listbox-item';
const CSS_PREFIX_BACK = 'vds-listbox-item-back';
const CSS_STATE = 'vds-state';

const CybsListboxItem = forwardRef(
  (
    {
      children,
      className,
      customCSS,
      dataTest,
      disabled,
      focus,
      hasSubitems,
      i18n,
      isActive,
      isSelected,
      level,
      itemChildren,
      listItemLabel,
      listItemValue,
      role,
      type,
      isWarning,
      lang,
      ...props
    },
    ref
  ) => {
    // eslint-disable-next-line react/prop-types
    const { templateLabel, index, value, label, isBack, ...remainingProps } = props;
    // Returns item content
    let itemContent = null;
    switch (type) {
      case 'multi':
        itemContent = (
          <span
            lang={lang}
            className={`vds-checkbox-label${
              isSelected ? ' vds-state--checked' : ''
            }`}
          >
            {listItemLabel}
          </span>
        );
        break;
      case 'single':
        itemContent = <span lang={lang}>{listItemLabel}</span>;
        break;
      default:
        if (isBack) {
          itemContent =
            (
              <React.Fragment>
                <CybsIcon
                  dataTest={`${dataTest}-arrow-previous`}
                  name="arrow-previous"
                  resolution="tiny"
                  tabIndex="-1"
                />
                <span className="cybs-listbox-item-back" lang={lang}>{listItemLabel}</span>
              </React.Fragment>
            );
        } else if (itemChildren) {
          itemContent =
          (
            <React.Fragment>
              <span className="cybs-listbox-item-nested" lang={lang}>{listItemLabel}</span>
              <CybsIcon
                dataTest={`${dataTest}-arrow-next`}
                name="arrow-next"
                resolution="tiny"
                tabIndex="-1"
              />
            </React.Fragment>
          );
        } else {
          itemContent = (<span lang={lang}>{listItemLabel}</span>);
        }
    }

    return (
      <li
        css={theme => [customCSS(theme)]}
        className={classnames(
          isBack ? CSS_PREFIX_BACK : CSS_PREFIX,
          {
            [`${CSS_STATE}--active`]: Boolean(isActive),
            [`${CSS_STATE}--disabled`]: Boolean(disabled),
            [`${CSS_STATE}--focus`]: Boolean(focus),
            [`${CSS_STATE}--selected`]: Boolean(
              type === 'single' && isSelected
            ),
            [`${CSS_STATE}--nested-selected`]: Boolean(
              type === 'nested' && isSelected
            ),
            'cybs-listbox-item--warning': Boolean(isWarning)
          },
          className
        )}
        data-index={index}
        data-list-item-label={templateLabel?.label ?? listItemLabel}
        ref={ref}
        role={role}
        {...remainingProps}
      >
        <React.Fragment>
          {children}
          {itemContent}
          {hasSubitems && (
            <span
              lang={lang}
              className="cybs-a11y-hidden"
              id={`item-category-${listItemValue || value}`}
            >
              {`${listItemLabel} ${i18n.category}`}
            </span>
          )}
        </React.Fragment>
      </li>
    );
  }
);

CybsListboxItem.propTypes = {
  /**
   * If `true`, the item has been previously selected.
   */
  isActive: PropTypes.bool,

  /**
   * Strings for the component.
   */
  i18n: PropTypes.shape({
    category: PropTypes.string
  }),

  /**
   * List of items in the listbox.
   */
  children: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }),
  /**
   * @ignore
   */
  itemChildren: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ),
  /**
   * lang tag for localized list
   */
   lang: PropTypes.string,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * Custom CSS in JavaScript CSS format
   */
  customCSS: PropTypes.func,

  /**
   * dataTest string for testing.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * Disabled
   */
  disabled: PropTypes.bool,

  /**
   * Focus
   */
  focus: PropTypes.bool,

  /**
   * Determines if this is a category header (with subh-items)
   */
  hasSubitems: PropTypes.bool,

  /**
   *  @ignore
   */
  listItemValue: PropTypes.string,

  /**
   *  @ignore
   */
  listItemLabel: PropTypes.oneOf(PropTypes.string, PropTypes.func),

  /**
   * Level of the nested dropdown that the list item belongs to. An item with level 1 has one parent menu.
   */
  level: PropTypes.number,

  /**
   * Role
   */
  role: PropTypes.string,

  /**
   * If `true`, the list item is selected.
   */
  isSelected: PropTypes.bool,

  /**
   * If `true`, the list item should have a warning background color.
   */
  isWarning: PropTypes.bool,

  /**
   * Different type of dropdowns.
   */
  type: PropTypes.oneOf(['multi', 'single', 'nested'])
};

CybsListboxItem.defaultProps = {
  isActive: false,
  children: undefined,
  itemChildren: undefined,
  className: '',
  customCSS: () => '',
  disabled: false,
  focus: false,
  isSelected: false,
  isWarning: false,
  hasSubitems: false,
  i18n: { category: 'category' },
  level: undefined,
  listItemLabel: '',
  listItemValue: '',
  role: 'option',
  type: 'single',
  lang: 'en'
};

CybsListboxItem.displayName = 'CybsListboxItem';

export default CybsListboxItem;
